import React, { useState } from 'react';
import { Container, Stack } from '@mui/material';
import { Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Page } from '../../../../components';
import HeaderBreadcrumbs from '../../../../components/header-breadcrumbs';
import { permissionCheck } from '../../../../helpers/permissionCheck';
import { customerPermissionFlags } from '../../../../constants/systemPermissions.constants';
import AreYouSureModal from '../../../../components/are-you-sure-modal';
import { bulkActionsApi } from '../../../../services/bulk-actions/bulkActionsApi';
import { handleApiError } from '../../../../services/react-query/react-query.client';
import { BulkUpdatePreviewResponse, BulkUpdateCommitResponse } from '../../../../types/bulkActions.types';
import { useEffectiveSettings, SETTING } from '../../../../hooks/use-effective-settings';

import { UploadStep } from './UploadStep';
import { PreviewStep } from './PreviewStep';
import { ResultStep } from './ResultStep';
import { formatTimeEstimate } from '../delete-users/timeEstimate';

type Step = 'upload' | 'preview' | 'result';

const getConfirmDescription = (count: number, isCommitting: boolean): string => {
    const userLabel = `${count} user${count !== 1 ? 's' : ''}`;
    const estimate = formatTimeEstimate(count);

    if (isCommitting) {
        return estimate
            ? `Updating ${userLabel}. Estimated time: ${estimate}. Please do not close this page.`
            : `Updating ${userLabel}. Please do not close this page.`;
    }

    return estimate
        ? `You are about to update ${userLabel}. Estimated time: ${estimate}. Are you sure you want to proceed?`
        : `You are about to update ${userLabel}. Are you sure you want to proceed?`;
};

export const BulkUpdateUsersScreen: React.FC = () => {
    const canAccess = permissionCheck(customerPermissionFlags.SUPER_ADMIN);

    const { getSetting, isLoading: isSettingsLoading } = useEffectiveSettings();
    const userUniqueIdentifier = getSetting(SETTING.userUniqueIdentifier, '');

    // Step flow state
    const [step, setStep] = useState<Step>('upload');
    const [attemptId, setAttemptId] = useState<string | null>(null);
    const [previewData, setPreviewData] = useState<BulkUpdatePreviewResponse | null>(null);
    const [commitResult, setCommitResult] = useState<BulkUpdateCommitResponse | null>(null);

    // Confirm modal state
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [isCommitting, setIsCommitting] = useState(false);

    if (!canAccess) {
        return <Navigate to="/" replace />;
    }

    const resetFlow = () => {
        setStep('upload');
        setAttemptId(null);
        setPreviewData(null);
        setCommitResult(null);
        setShowConfirmModal(false);
        setIsCommitting(false);
    };

    const handlePreviewComplete = (data: BulkUpdatePreviewResponse) => {
        setPreviewData(data);
        setAttemptId(data.attemptId);
        setStep('preview');
    };

    const handleContinue = () => {
        setShowConfirmModal(true);
    };

    const handleConfirmUpdate = async () => {
        if (!attemptId) return;

        setIsCommitting(true);
        try {
            const result = await bulkActionsApi.updateUsersCommit(attemptId);
            setCommitResult(result);
            setShowConfirmModal(false);
            setStep('result');
        } catch (err: any) {
            const msg = handleApiError(err);

            if (err?.response?.status === 410 || msg.includes('PREVIEW_EXPIRED')) {
                toast.error('Preview has expired. Please start over with a new upload.');
                resetFlow();
            } else {
                toast.error(`Update failed: ${msg}`);
            }

            setShowConfirmModal(false);
        } finally {
            setIsCommitting(false);
        }
    };

    const totalReadyCount = previewData?.summary.readyToUpdateCount || 0;

    const renderStep = () => {
        switch (step) {
            case 'upload':
                return (
                    <UploadStep
                        identifierField={userUniqueIdentifier || 'INVITECODE'}
                        isSettingsLoading={isSettingsLoading}
                        onPreviewComplete={handlePreviewComplete}
                    />
                );
            case 'preview':
                if (!previewData) return null;
                return (
                    <PreviewStep
                        previewData={previewData}
                        onContinue={handleContinue}
                        onBack={resetFlow}
                    />
                );
            case 'result':
                if (!commitResult || !attemptId) return null;
                return (
                    <ResultStep
                        commitResult={commitResult}
                        attemptId={attemptId}
                        onStartNew={resetFlow}
                    />
                );
        }
    };

    return (
        <Page title="POM - Bulk Update Users">
            <Container maxWidth={false}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <HeaderBreadcrumbs
                        heading="Bulk Update Users"
                        links={[
                            { name: 'Bulk Actions', href: '/bulk-actions' },
                            { name: 'Update Users', href: '/bulk-actions/update-users' },
                        ]}
                        activeLast
                    />
                </Stack>

                {renderStep()}

                <AreYouSureModal
                    visible={showConfirmModal}
                    loading={isCommitting}
                    title="Confirm Bulk Update"
                    description={getConfirmDescription(totalReadyCount, isCommitting)}
                    color="primary"
                    onConfirm={handleConfirmUpdate}
                    onHide={() => !isCommitting && setShowConfirmModal(false)}
                />
            </Container>
        </Page>
    );
};
